import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    startTransition,
    Suspense,
    type FC,
} from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useRefetchableFragment, useRelayEnvironment } from 'react-relay';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { getBuyerId, getUserType, getPriceBookName } from 'dibs-cookie-jar';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly.ts';
import { ErrorBoundary } from 'dibs-error-tools/ErrorBoundary.tsx';

import throttle from 'lodash/throttle.js';
import { Button } from 'dibs-elements/exports/Button.tsx';

import { TopBar } from '../TopBar/TopBar.jsx';
import { BottomBar } from '../BottomBar/BottomBar.tsx';
import Drawer from '../Drawer/Drawer.jsx';
import { MessageBannerLazy } from '../MessageBanner/MessageBannerLazy.tsx';

import { actionCreators } from '../../actions/index.ts';
import * as userTracking from '../../utils/userTracking.js';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import {
    hasValidUserSessionGeoInfo,
    getUserSessionCountryCode,
    getUserZipCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { getGeoLocationPromoGroupFromLocalStorage } from 'dibs-display-promo-code/exports/promoHelpers.ts';
import { getLocalHistoryItemIds } from 'dibs-recent-history/exports/getLocalHistoryItemIds.ts';
import { MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS } from 'dibs-recent-history/exports/constants.ts';
import { TRADE } from '../../../exports/userTypeEnums.ts';
import {
    getCurrencyPreference,
    setSessionCurrency,
    setLocalCurrency,
    getSessionCurrency,
} from '../../utils/preferredCurrency.js';
import { PriceIntlHandler } from '../PriceIntlHandler/PriceIntlHandler.tsx';
import { loginUserWithEmailLoginToken } from '../../helpers/loginUserWithEmailLoginTokenHelper.ts';
import { getEmailLoginToken } from '../../helpers/getEmailLoginTokenHelper.ts';
import { isUpdatedNotificationContentVariant } from '../../helpers/notificationsTestHelpers.ts';

import dibsCss from 'dibs-css';
import styles from './Header-style.scss';

import { type Header_viewer$key } from './__generated__/Header_viewer.graphql.ts';

const MESSAGE_BANNER_HEIGHT = 42;
const SCROLL_THROTTLE_DURATION = 10;

type HeaderProps = {
    viewer: Header_viewer$key;
};

type HeaderFragmentVariables = {
    hasUserId: boolean;
    hasTradeUserId: boolean;
    userId: string;
    userIds: string[];
    fetchRegionalInfo: boolean;
    zipCode: string;
    isClient?: boolean;
    userCountryCode?: string;
    localRecentHistoryItemIds?: string[];
    fetchUserRecentHistoryItem?: boolean;
    priceBookName?: string;
    isGeoLocationPromoGroupSet?: boolean;
    isUpdatedNotificationContentVariant?: boolean;
};

type HeaderReduxState = {
    header: {
        disableStickyNav: boolean;
        hideBottomBar: boolean;
        hideHeader: boolean;
        updateUserState: boolean;
        initialTrackingFired: boolean;
        isUserTypeTrade: boolean;
        showMarketingMessages: boolean;
    };
    drawer: {
        isDrawerOpen: boolean;
    };
};

type HeaderStateProps = {
    disableStickyNav: boolean;
    hideBottomBar: boolean;
    hideHeader: boolean;
    updateUserState: boolean;
    initialTrackingFired: boolean;
    isUserTypeTrade: boolean;
    showMarketingMessages: boolean;
    isDrawerOpen: boolean;
};

const selectHeaderState: (state: HeaderReduxState) => HeaderStateProps = ({ header, drawer }) => ({
    disableStickyNav: header.disableStickyNav,
    hideBottomBar: header.hideBottomBar,
    hideHeader: header.hideHeader,
    updateUserState: header.updateUserState,
    initialTrackingFired: header.initialTrackingFired,
    isUserTypeTrade: header.isUserTypeTrade,
    showMarketingMessages: header.showMarketingMessages,
    isDrawerOpen: drawer.isDrawerOpen,
});

const viewerFragment = graphql`
    fragment Header_viewer on Viewer @refetchable(queryName: "HeaderRefetchQuery") {
        # component's isClient and relay's $isClient is out of sync
        # this var can be used to ensure we have issued a refetch and all the fragments are in place
        relayIsClient: __typename @include(if: $isClient)
        ...TopBar_viewer
        ...userTracking_viewer @defer @include(if: $isClient)
        ...BottomBar_viewer
        user(userId: $userId) @include(if: $hasUserId) {
            ...userTracking_user @defer
            ...TopBar_user
            isVip
            isVerifiedTrade
            preferences {
                currency
            }
        }
        regionalInfo(userId: $userId, countryCode: $userCountryCode, zipCode: $zipCode)
            @include(if: $fetchRegionalInfo) {
            ...userTracking_regionalInfo @relay(mask: false)
            currency
        }
    }
`;

export const Header: FC<HeaderProps> = ({ viewer: viewerRef }) => {
    const dispatch = useDispatch();
    const environment = useRelayEnvironment();
    const [viewer, refetch] = useRefetchableFragment(viewerFragment, viewerRef);
    const { relayIsClient } = viewer;
    const {
        disableStickyNav,
        hideHeader,
        showMarketingMessages,
        hideBottomBar,
        isDrawerOpen,
        isUserTypeTrade,
        updateUserState,
        initialTrackingFired,
    } = useSelector<HeaderReduxState, HeaderStateProps>(selectHeaderState, shallowEqual);

    const isClient = useClientState();
    const [hasError, setHasError] = useState(false);
    const [forceSlideIn, setForceSlideIn] = useState(false);
    const [isBannerOnScreen, setIsBannerOnScreen] = useState(
        showMarketingMessages && !disableStickyNav
    );

    const [fragmentVariables, setFragmentVariables] = useState<HeaderFragmentVariables>({
        hasUserId: false,
        hasTradeUserId: false,
        userId: '',
        userIds: [],
        fetchRegionalInfo: false,
        zipCode: '',
    });
    const hasSentLoginMutationRef = useRef(false);
    const prevUpdateUserStateRef = useRef(updateUserState);
    const prevUserRef = useRef(viewer?.user);
    const fragmentVariablesRef = useRef(fragmentVariables);
    const latestViewerRef = useRef(viewer);
    const hasInitiallyViewerFetched = useRef(false);
    const hasMessageBanner = showMarketingMessages && !disableStickyNav;

    const setInitialTrackingFired = useCallback(() => {
        dispatch(actionCreators.setInitialTrackingFired());
    }, [dispatch]);
    const setDrawerState = useCallback(
        (drawerOpen: boolean) => {
            dispatch(actionCreators.setDrawerState(drawerOpen));
        },
        [dispatch]
    );

    useEffect(() => {
        fragmentVariablesRef.current = fragmentVariables;
    }, [fragmentVariables]);

    useEffect(() => {
        latestViewerRef.current = viewer;
    }, [viewer]);

    const setupCurrencyInfo = useCallback(() => {
        const currentViewer = latestViewerRef.current;
        const user = currentViewer?.user;
        const userCurrency = user?.preferences?.currency;
        const regionalCurrency = currentViewer?.regionalInfo?.[0]?.currency;

        if (!user && !getSessionCurrency()) {
            setSessionCurrency(regionalCurrency);
        } else if (userCurrency) {
            setLocalCurrency(userCurrency);
        }
        dispatch(
            actionCreators.populateCurrency(
                getCurrencyPreference({
                    user: user || undefined,
                    regionalCurrency: regionalCurrency || undefined,
                })
            )
        );
    }, [dispatch]);

    /**
     * The viewer should be fetched a max of (2) times:
     *
     * (1) On component mount to fetch client-only fragments, and user fragments if already logged in
     * (2) On update if user logs in
     */
    const refetchViewer = useCallback(() => {
        const userId = getBuyerId(document.cookie) || '';
        const cookieUserType = getUserType(document.cookie) || '';
        const hasUserId = !!userId;
        const fetchRegionalInfo =
            (!hasUserId && !getSessionCurrency()) || !hasValidUserSessionGeoInfo();
        const isTradeUser = cookieUserType === TRADE || isUserTypeTrade;
        const hasTradeUserId = hasUserId && isTradeUser;
        dispatch(actionCreators.setIsEmailOnly(isEmailOnly(document.cookie)));
        const localRecentHistoryItemIds = getLocalHistoryItemIds({ isClient: true });

        const nextFragmentVariables = {
            ...fragmentVariablesRef.current,
            hasUserId,
            hasTradeUserId,
            userId,
            userIds: [userId],
            isClient: true,
            fetchRegionalInfo,
            userCountryCode: getUserSessionCountryCode(),
            zipCode: getUserZipCode(),
            localRecentHistoryItemIds,
            fetchUserRecentHistoryItem:
                localRecentHistoryItemIds.length < MAX_CAROUSEL_RECENTLY_VIEWED_ITEMS,
            priceBookName: getPriceBookName(document.cookie) || 'DEFAULT',
            isGeoLocationPromoGroupSet: !!getGeoLocationPromoGroupFromLocalStorage().length,
            isUpdatedNotificationContentVariant: isUpdatedNotificationContentVariant(),
        };

        fragmentVariablesRef.current = nextFragmentVariables;
        setFragmentVariables(nextFragmentVariables);
        startTransition(() => {
            refetch(nextFragmentVariables, { onComplete: setupCurrencyInfo });
        });
    }, [dispatch, isUserTypeTrade, refetch, setupCurrencyInfo]);

    const handleScroll = useMemo(
        () =>
            throttle(() => {
                setIsBannerOnScreen(prevIsBannerOnScreen => {
                    if (Drawer.getWindowScrollY() < MESSAGE_BANNER_HEIGHT) {
                        return true;
                    }
                    if (prevIsBannerOnScreen) {
                        return false;
                    }
                    return prevIsBannerOnScreen;
                });
            }, SCROLL_THROTTLE_DURATION),
        []
    );

    useEffect(() => {
        if (hasInitiallyViewerFetched.current) {
            return;
        }
        hasInitiallyViewerFetched.current = true;
        refetchViewer();
    }, [refetchViewer]);

    useEffect(() => {
        if (!hasError && hasMessageBanner) {
            handleScroll();
            window.addEventListener('scroll', handleScroll);
        }
        return () => {
            window.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, [handleScroll, hasError, hasMessageBanner]);

    useEffect(() => {
        if (updateUserState && !prevUpdateUserStateRef.current) {
            refetchViewer();
        }
        prevUpdateUserStateRef.current = updateUserState;
    }, [refetchViewer, updateUserState]);

    useEffect(() => {
        const user = viewer?.user;
        if (!prevUserRef.current) {
            const { isVerifiedTrade, isVip } = user || {};
            if (isVerifiedTrade) {
                dispatch(actionCreators.setUserTypeTrade(true));
            }
            if (isVip) {
                dispatch(actionCreators.setUserTypeVip(true));
            }
        }
        prevUserRef.current = user;
    }, [dispatch, viewer]);

    useEffect(() => {
        const userId = getBuyerId(document.cookie) || '';
        const emailLoginToken = getEmailLoginToken();
        const shouldLoginUser = !hasSentLoginMutationRef.current && !userId && !!emailLoginToken;
        if (shouldLoginUser) {
            hasSentLoginMutationRef.current = true;
            loginUserWithEmailLoginToken(environment, emailLoginToken, refetchViewer);
        }
    }, [environment, refetchViewer]);

    const handleTopBarMouseOver = useCallback(() => {
        if (!forceSlideIn) {
            setForceSlideIn(true);
        }
    }, [forceSlideIn]);

    const handleTopBarMouseOut = useCallback(() => {
        if (forceSlideIn) {
            setForceSlideIn(false);
        }
    }, [forceSlideIn]);

    const user = viewer?.user || null;
    const regionalInfo = viewer?.regionalInfo?.[0] || null;
    const { hasUserId, userId, fetchRegionalInfo, hasTradeUserId } = fragmentVariables;
    const hasUser = !!user;
    const hasRootPendingRefetch =
        (!hasUser && hasUserId) || (fetchRegionalInfo && !viewer?.regionalInfo);
    const forceDisableStickyNav = hasError;

    const disableSticky = disableStickyNav || forceDisableStickyNav;

    const isBannerOutOfScreen = showMarketingMessages && !disableSticky && !isBannerOnScreen;

    const isSticky = ((!hasMessageBanner || isBannerOutOfScreen) && !isDrawerOpen) || hideBottomBar;
    const errorHeader = (
        <header
            className={classnames(styles.wrapper, dibsCss.zNav, dibsCss.relative, dibsCss.bgWhite)}
            hidden={hideHeader}
        >
            <div
                className={classnames(
                    styles.header,
                    styles.hasTopBarOnly,
                    dibsCss.fixed,
                    dibsCss.wFull,
                    dibsCss.zNav,
                    {
                        [styles.hasBanner]: showMarketingMessages,
                        [styles.isBannerOutOfScreen]: isBannerOutOfScreen,
                        [styles.disableSticky]: disableSticky,
                        [styles.hideBottomBar]: hideBottomBar,
                    }
                )}
            >
                <TopBar
                    hasError
                    hasRootPendingRefetch={hasRootPendingRefetch}
                    hasUser={hasUser}
                    hasUserId={hasUserId}
                    viewer={viewer}
                    isClient={isClient}
                    userId={userId}
                    user={user}
                    isSticky={isSticky}
                />
            </div>
            <div
                className={classnames(
                    styles.shadow,
                    styles.hasTopBarOnly,
                    dibsCss.relative,
                    disableSticky && dibsCss.hidden,
                    {
                        [styles.isBannerOutOfScreen]: isBannerOutOfScreen,
                        [styles.hideBottomBar]: hideBottomBar,
                    }
                )}
            />
        </header>
    );

    if (hasError) {
        return errorHeader;
    }

    const BottomWrapperTag = disableSticky ? 'div' : Drawer;
    const bottomWrapperProps = disableSticky ? {} : { onStateChange: setDrawerState, forceSlideIn };
    const BottomBarContent = hideBottomBar ? (
        <div id="nav-bottom-marker" />
    ) : (
        <BottomWrapperTag
            className={classnames(
                (disableSticky || !isBannerOutOfScreen) && [
                    dibsCss.relative,
                    dibsCss.borderB,
                    dibsCss.borderBDolphin,
                    dibsCss.borderBSolid,
                ]
            )}
            {...bottomWrapperProps}
        >
            <BottomBar viewer={viewer} />
            <div id="nav-bottom-marker" />
        </BottomWrapperTag>
    );

    return (
        <ErrorBoundary logFn={() => setHasError(true)} fallback={() => errorHeader}>
            <header
                className={classnames(
                    // header z-index: 980, this is the same as the nav z-index
                    // if the z-index is not set at 0 or above, the marketing banner, which itself has a z-index of -1, "sinks" and becomes unresponsive even when visible
                    // given that this wrapper has a z-index set at all, it must be set to 100 or more to stay above similar items images
                    styles.wrapper,
                    dibsCss.zNav,
                    dibsCss.relative,
                    dibsCss.bgWhite
                )}
                hidden={hideHeader}
            >
                <Button
                    className={classnames(
                        styles.skipToContent,
                        dibsCss.fixed,
                        dibsCss.topXsmall,
                        dibsCss.leftXsmall
                    )}
                    type="secondaryAlt"
                    // use href to force anchor tag to render bc the button is for navigation and
                    // anchor is better semantically. prevent default to allow more control in
                    // navigation
                    href="#mainContent"
                    onClick={() => {
                        if (typeof document !== 'undefined') {
                            const header = document.getElementById('js-header');
                            const mainContent = document.getElementById('mainContent');
                            // main needs an offset bc when scrolling to, the target is aligned to top of the page, which is
                            // covered by the floating header. programmatically determine offset bc header has variable height.
                            if (header && mainContent) {
                                const offset = header.offsetHeight + 'px';
                                mainContent.style.cssText = `margin-top: -${offset}; padding-top: ${offset};`;
                            }
                        }
                    }}
                >
                    <FormattedMessage
                        id="dbl.Header.SkipToContent"
                        defaultMessage="Skip to main content"
                    />
                </Button>
                <div
                    className={classnames(
                        styles.header,
                        dibsCss.fixed,
                        dibsCss.wFull,
                        dibsCss.zNav,
                        {
                            [styles.hasBanner]: showMarketingMessages,
                            [styles.isBannerOutOfScreen]: isBannerOutOfScreen,
                            [styles.disableSticky]: disableSticky,
                            [styles.hideBottomBar]: hideBottomBar,
                        }
                    )}
                >
                    {showMarketingMessages && (
                        <MessageBannerLazy
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            relayEnvironment={environment}
                            userId={userId}
                            hasUserId={hasUserId}
                            isClient={isClient}
                        />
                    )}

                    <TopBar
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUser={hasUser}
                        hasUserId={hasUserId}
                        viewer={viewer}
                        isClient={isClient}
                        userId={userId}
                        user={user}
                        regionalInfo={regionalInfo}
                        onMouseOver={handleTopBarMouseOver}
                        onMouseOut={handleTopBarMouseOut}
                        isSticky={isSticky}
                        hasTradeUserId={hasTradeUserId}
                    />
                    {BottomBarContent}
                </div>
                <div
                    className={classnames(styles.shadow, dibsCss.relative, {
                        [styles.isBannerOutOfScreen]: isBannerOutOfScreen,
                        [styles.hideBottomBar]: hideBottomBar,
                        [dibsCss.hidden]: disableSticky,
                    })}
                />
                <PriceIntlHandler />
                {relayIsClient && (
                    <Suspense fallback={''}>
                        <userTracking.UserTrackingInit
                            viewer={viewer}
                            user={user}
                            environment={environment}
                            initialTrackingFired={initialTrackingFired}
                            setInitialTrackingFired={setInitialTrackingFired}
                        />
                    </Suspense>
                )}
            </header>
        </ErrorBoundary>
    );
};
